import React, { useState, useEffect } from 'react';
import { Input } from 'src/components/ui/input';
import { Button } from 'src/components/ui/button';
import {
  Loader2,
  Plus,
  Trash2,
  Info,
  BookOpen,
  Scale,
  Target,
  Eye,
  EyeOff,
  Check,
  X,
  Upload,
  GripVertical,
  ChevronDown,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { TestGroupSelectionModal } from 'src/components/Modals/TestGroupSelectionModal';
import { TestService } from 'src/services/api/TestService';
import { useAssessmentTypes } from 'src/services/api/queries/assessmentTypeQueries';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from 'src/components/ui/chart';

const fieldBase =
  'w-full px-3 py-[9px] rounded-lg border border-input text-[13px] text-foreground-strong bg-card placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] disabled:opacity-50 disabled:cursor-not-allowed';

export const NewTestCollection = ({
  onSubmit,
  initialData,
  loading = false,
  viewOnly = false,
}) => {
  const { toast } = useToast();
  const isEditMode = Boolean(initialData?.id);

  // Group-level "Test type" dropdown — optional, sourced from GET
  // /internal/assessment-types via the existing AssessmentTypeService/
  // useAssessmentTypes hook (already used by the Assessment Types admin
  // page). Only types that are both active and enabled are selectable;
  // the unfiltered list is kept too so a previously-picked, since-disabled
  // type still shows its display_name instead of a raw code.
  const { data: assessmentTypesData } = useAssessmentTypes();
  const assessmentTypes =
    assessmentTypesData?.data ?? assessmentTypesData ?? [];
  const selectableAssessmentTypes = assessmentTypes.filter(
    (type) => type.is_active && type.enabled
  );
  const getAssessmentTypeLabel = (code) =>
    assessmentTypes.find((type) => type.code === code)?.display_name || code;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    collection_id: '',
    is_active: true,
    groups: [],
    cutoff_score: 50,
  });
  const [showChart, setShowChart] = useState(false);
  const [showWeightageGuide, setShowWeightageGuide] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showRemoveGroupDialog, setShowRemoveGroupDialog] = useState(false);
  const [groupToRemove, setGroupToRemove] = useState(null);
  const [activeTab, setActiveTab] = useState('groups');
  const [expandedGroupId, setExpandedGroupId] = useState(null);
  const [groupTests, setGroupTests] = useState({});
  const [loadingTests, setLoadingTests] = useState({});
  const [testWeightages, setTestWeightages] = useState({});
  const [groupRules, setGroupRules] = useState({});
  const [ruleErrors, setRuleErrors] = useState({});
  const [showRuleSyntaxGuide, setShowRuleSyntaxGuide] = useState(false);
  const [initialFormData, setInitialFormData] = useState(null);
  const [initialTestWeightages, setInitialTestWeightages] = useState(null);
  const [initialGroupRules, setInitialGroupRules] = useState(null);
  const [unsavedChangesCount, setUnsavedChangesCount] = useState(0);
  const [isUpdatingRules, setIsUpdatingRules] = useState(false);
  const [hasUnsavedRuleChanges, setHasUnsavedRuleChanges] = useState(false);
  const [hasReordered, setHasReordered] = useState(false);

  const toggleGroupExpand = (groupId) => {
    setExpandedGroupId((prev) => (prev === groupId ? null : groupId));
  };

  const handleTestRefChange = (groupIndex, value) => {
    const updatedGroups = [...formData.groups];
    updatedGroups[groupIndex] = {
      ...updatedGroups[groupIndex],
      test_ref: value,
    };
    setFormData({ ...formData, groups: updatedGroups });
  };

  const handleTestTypeCodeChange = (groupIndex, value) => {
    const updatedGroups = [...formData.groups];
    updatedGroups[groupIndex] = {
      ...updatedGroups[groupIndex],
      test_type_code: value,
    };
    setFormData({ ...formData, groups: updatedGroups });
  };

  const validateRule = (rule, totalGroups, currentGroupIndex = null) => {
    if (!rule || rule.trim() === '') {
      return { isValid: true, error: null };
    }

    const errors = [];

    let bracketCount = 0;
    for (let i = 0; i < rule.length; i += 1) {
      if (rule[i] === '(') {
        bracketCount += 1;
      } else if (rule[i] === ')') {
        bracketCount -= 1;
        if (bracketCount < 0) {
          errors.push('Closing bracket without matching opening bracket');
          break;
        }
      }
    }

    if (bracketCount > 0) {
      errors.push('Missing closing bracket(s)');
    } else if (bracketCount < 0 && errors.length === 0) {
      errors.push('Missing opening bracket(s)');
    }

    const lowercaseOperators = rule.match(/\b(and|or)\b/gi);
    if (lowercaseOperators) {
      const invalidOperators = lowercaseOperators.filter(
        (op) => op === 'and' || op === 'or'
      );
      if (invalidOperators.length > 0) {
        const uniqueInvalidOps = [...new Set(invalidOperators)];
        errors.push(
          `Operators must be in UPPERCASE. Found: ${uniqueInvalidOps.join(', ')}. Use ${uniqueInvalidOps.map((op) => op.toUpperCase()).join(', ')} instead`
        );
      }
    }

    const lowercaseGroupRefs = rule.match(/\bg\d+\b/gi);
    if (lowercaseGroupRefs) {
      const invalidGroupRefs = lowercaseGroupRefs.filter((ref) =>
        ref.startsWith('g')
      );
      if (invalidGroupRefs.length > 0) {
        const uniqueInvalidGroupRefs = [...new Set(invalidGroupRefs)];
        errors.push(
          `Group references must use UPPERCASE 'G'. Found: ${uniqueInvalidGroupRefs.join(', ')}. Use ${uniqueInvalidGroupRefs.map((ref) => ref.toUpperCase()).join(', ')} instead`
        );
      }
    }

    const groupRefs = rule.match(/G\d+/g);
    if (!groupRefs && rule.trim() !== '') {
      errors.push('Rule must contain group references (e.g., G1, G2)');
    } else if (groupRefs) {
      const invalidRefs = [];
      const outOfRangeRefs = [];

      groupRefs.forEach((ref) => {
        const groupNumber = parseInt(ref.substring(1), 10);
        if (groupNumber <= 0) {
          invalidRefs.push(ref);
        } else if (groupNumber > totalGroups) {
          outOfRangeRefs.push(ref);
        }
      });

      if (invalidRefs.length > 0) {
        const uniqueInvalidRefs = [...new Set(invalidRefs)];
        errors.push(
          `Invalid group reference(s): ${uniqueInvalidRefs.join(', ')}. Group references must start from G1`
        );
      }

      if (outOfRangeRefs.length > 0) {
        const uniqueOutOfRangeRefs = [...new Set(outOfRangeRefs)];
        errors.push(
          `Group reference(s) out of range: ${uniqueOutOfRangeRefs.join(', ')}. You only have ${totalGroups} group${totalGroups > 1 ? 's' : ''} (G1${totalGroups > 1 ? ` to G${totalGroups}` : ''})`
        );
      }

      if (currentGroupIndex !== null) {
        const forwardRefs = [];
        groupRefs.forEach((ref) => {
          const groupNumber = parseInt(ref.substring(1), 10);
          if (groupNumber > currentGroupIndex + 1) {
            forwardRefs.push(ref);
          }
        });

        if (forwardRefs.length > 0) {
          const uniqueForwardRefs = [...new Set(forwardRefs)];
          const currentGroupNumber = currentGroupIndex + 1;
          errors.push(
            `Group G${currentGroupNumber} cannot reference groups that come after it. Found: ${uniqueForwardRefs.join(', ')}. G${currentGroupNumber} can only reference G1 to G${currentGroupNumber}`
          );
        }
      }
    }

    return {
      isValid: errors.length === 0,
      error: errors.length > 0 ? errors.join('; ') : null,
    };
  };

  const handleRuleChange = (groupId, rule) => {
    setGroupRules((prev) => ({
      ...prev,
      [groupId]: rule,
    }));

    const totalGroups = formData.groups.length;
    const currentGroupIndex = formData.groups.findIndex(
      (g) => String(g.group_id) === String(groupId)
    );

    const validation =
      currentGroupIndex >= 0
        ? validateRule(rule, totalGroups, currentGroupIndex)
        : validateRule(rule, totalGroups);

    setRuleErrors((prev) => ({
      ...prev,
      [groupId]: validation.error,
    }));
  };

  const fetchGroupTests = async (groupId) => {
    setLoadingTests((prev) => ({ ...prev, [groupId]: true }));

    try {
      const response = await TestService.getTestGroup(groupId);
      const tests = response?.data?.tests || [];

      setGroupTests((prev) => ({
        ...prev,
        [groupId]: tests,
      }));

      const initialWeightages = {};
      tests.forEach((test) => {
        initialWeightages[test.id] = {
          group_score_weight: 1,
          total_score_weight: 1,
        };
      });

      setTestWeightages((prev) => ({
        ...prev,
        [groupId]: initialWeightages,
      }));
    } catch (error) {
      console.error(`Error fetching tests for group ${groupId}:`, error);
      toast({
        title: 'Error',
        description: `Failed to fetch tests for group`,
        variant: 'destructive',
      });
      setGroupTests((prev) => ({
        ...prev,
        [groupId]: [],
      }));
    } finally {
      setLoadingTests((prev) => ({ ...prev, [groupId]: false }));
    }
  };

  useEffect(() => {
    if (initialData && isEditMode) {
      const transformedGroups = (initialData.groups || []).map(
        (group, index) => ({
          group_id: group.id,
          name: group.name,
          description: group.description,
          test_ref: group.test_ref || '',
          test_type_code: group.test_type_code || '',
          order_number: group.order_number || index + 1,
        })
      );

      const formDataToSet = {
        name: initialData.name || '',
        description: initialData.description || '',
        collection_id: initialData.id || '',
        is_active: initialData.status === 'ACTIVE',
        groups: transformedGroups,
        cutoff_score: initialData.cutoff_score ?? 50,
      };

      setFormData(formDataToSet);
      setInitialFormData(JSON.parse(JSON.stringify(formDataToSet)));

      const newGroupTests = {};
      const newTestWeightages = {};

      (initialData.groups || []).forEach((group) => {
        const groupId = group.id;
        newGroupTests[groupId] = group.tests || [];

        const weightages = {};
        (group.tests || []).forEach((test) => {
          weightages[test.id] = {
            group_score_weight: test.group_score_weight || 1,
            total_score_weight: test.total_score_weight || 1,
          };
        });
        newTestWeightages[groupId] = weightages;
      });

      setGroupTests(newGroupTests);
      setTestWeightages(newTestWeightages);
      setInitialTestWeightages(JSON.parse(JSON.stringify(newTestWeightages)));

      const newRules = {};
      (initialData.groups || []).forEach((group) => {
        if (group.rule) {
          newRules[group.id] = group.rule;
        }
      });
      setGroupRules(newRules);
      setInitialGroupRules(JSON.parse(JSON.stringify(newRules)));
    }
  }, [initialData, isEditMode]);

  useEffect(() => {
    const totalGroups = formData.groups.length;
    const newErrors = {};

    Object.keys(groupRules).forEach((groupId) => {
      const rule = groupRules[groupId];
      if (rule && rule.trim() !== '') {
        const currentGroupIndex = formData.groups.findIndex(
          (g) => String(g.group_id) === String(groupId)
        );

        const validation =
          currentGroupIndex >= 0
            ? validateRule(rule, totalGroups, currentGroupIndex)
            : validateRule(rule, totalGroups);

        if (validation.error) {
          newErrors[groupId] = validation.error;
        }
      }
    });

    setRuleErrors(newErrors);
  }, [formData.groups.length, formData.groups, groupRules]);

  useEffect(() => {
    if (!isEditMode || !initialFormData || !initialTestWeightages) {
      return;
    }

    let changeCount = 0;

    if (formData.name !== initialFormData.name) changeCount += 1;
    if (formData.description !== initialFormData.description) changeCount += 1;
    if (
      parseFloat(formData.cutoff_score) !==
      parseFloat(initialFormData.cutoff_score)
    )
      changeCount += 1;

    const currentGroupIds = formData.groups.map((g) => g.group_id).sort();
    const initialGroupIds = initialFormData.groups
      .map((g) => g.group_id)
      .sort();

    if (JSON.stringify(currentGroupIds) !== JSON.stringify(initialGroupIds)) {
      changeCount += Math.abs(currentGroupIds.length - initialGroupIds.length);
    }

    if (hasReordered) changeCount += 1;

    formData.groups.forEach((group) => {
      const initialGroup = initialFormData.groups.find(
        (g) => g.group_id === group.group_id
      );
      if (initialGroup && group.test_ref !== initialGroup.test_ref) {
        changeCount += 1;
      }
      if (
        initialGroup &&
        group.test_type_code !== initialGroup.test_type_code
      ) {
        changeCount += 1;
      }
    });

    Object.keys(testWeightages).forEach((groupId) => {
      const currentWeightages = testWeightages[groupId] || {};
      const initialWeightagesForGroup = initialTestWeightages[groupId] || {};

      Object.keys(currentWeightages).forEach((testId) => {
        const current = currentWeightages[testId];
        const initial = initialWeightagesForGroup[testId];

        if (initial) {
          if (current.group_score_weight !== initial.group_score_weight)
            changeCount += 1;
          if (current.total_score_weight !== initial.total_score_weight)
            changeCount += 1;
        }
      });
    });

    if (initialGroupRules) {
      Object.keys(groupRules).forEach((groupId) => {
        const currentRule = groupRules[groupId] || '';
        const initialRule = initialGroupRules[groupId] || '';
        if (currentRule !== initialRule) changeCount += 1;
      });

      Object.keys(initialGroupRules).forEach((groupId) => {
        if (!groupRules[groupId] && initialGroupRules[groupId])
          changeCount += 1;
      });
    }

    setUnsavedChangesCount(changeCount);
  }, [
    formData,
    testWeightages,
    groupRules,
    initialFormData,
    initialTestWeightages,
    initialGroupRules,
    isEditMode,
    hasReordered,
  ]);

  useEffect(() => {
    if (!isEditMode || !initialGroupRules) {
      setHasUnsavedRuleChanges(false);
      return;
    }

    let hasChanges = false;

    Object.keys(groupRules).forEach((groupId) => {
      const currentRule = groupRules[groupId] || '';
      const initialRule = initialGroupRules[groupId] || '';
      if (currentRule !== initialRule) hasChanges = true;
    });

    Object.keys(initialGroupRules).forEach((groupId) => {
      if (!groupRules[groupId] && initialGroupRules[groupId]) hasChanges = true;
    });

    setHasUnsavedRuleChanges(hasChanges);
  }, [groupRules, initialGroupRules, isEditMode]);

  const handleTabChange = (newTab) => {
    if (
      activeTab === 'groups' &&
      formData.groups.length > 0 &&
      !formData.groups[0].test_ref
    ) {
      toast({
        title: 'Validation Error',
        description:
          'The first group must have a test reference before switching tabs',
        variant: 'destructive',
      });
      return;
    }

    if (hasReordered) {
      toast({
        title: 'Groups Reordered',
        description:
          'You have reordered the groups. Please save your changes before switching tabs.',
        variant: 'destructive',
      });
      return;
    }

    if (!isEditMode) {
      setActiveTab(newTab);
      return;
    }

    const hasUnsavedChanges =
      (activeTab === 'rules' && hasUnsavedRuleChanges) ||
      (activeTab !== 'rules' && unsavedChangesCount > 0);

    if (hasUnsavedChanges) {
      let tabName = 'Groups';
      if (activeTab === 'rules') tabName = 'Group Rule';
      else if (activeTab === 'tests') tabName = 'All Tests';

      toast({
        title: 'Unsaved Changes',
        description: `You have unsaved changes in the ${tabName} tab. Please save your changes before switching tabs.`,
        variant: 'destructive',
      });
      return;
    }

    setActiveTab(newTab);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (viewOnly) return;

    if (formData.groups.length > 0 && !formData.groups[0].test_ref) {
      toast({
        title: 'Validation Error',
        description: 'The first group must have a test reference',
        variant: 'destructive',
      });
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirmedSubmit = async () => {
    setShowConfirmDialog(false);

    const transformedData = {
      name: formData.name,
      description: formData.description,
      cutoff_score: parseFloat(formData.cutoff_score) || 0,
      groups: formData.groups.map((group) => ({
        id: group.group_id,
        test_ref: group.test_ref || '',
        test_type_code: group.test_type_code || '',
        order_number: group.order_number,
        tests: (groupTests[group.group_id] || []).map((test) => ({
          id: String(test.id),
          group_score_weight:
            testWeightages[group.group_id]?.[test.id]?.group_score_weight || 1,
          total_score_weight:
            testWeightages[group.group_id]?.[test.id]?.total_score_weight || 1,
        })),
      })),
    };

    setHasReordered(false);

    if (isEditMode) {
      setInitialFormData(JSON.parse(JSON.stringify(formData)));
      setInitialTestWeightages(JSON.parse(JSON.stringify(testWeightages)));
      setInitialGroupRules({});
    }

    onSubmit(transformedData);
  };

  const handleAddGroups = async (selectedGroups) => {
    const newGroups = selectedGroups.map((group, index) => ({
      group_id: group.id,
      test_ref: group.test_ref || '',
      test_type_code: group.test_type_code || '',
      name: group.name,
      description: group.description,
      order_number: formData.groups.length + index + 1,
      group_score_weight: 0,
      total_score_weight: 0,
    }));

    setFormData({
      ...formData,
      groups: [...formData.groups, ...newGroups],
    });

    newGroups.forEach((group) => {
      fetchGroupTests(group.group_id);
    });
  };

  const handleRemoveGroupClick = (index) => {
    setGroupToRemove(index);
    setShowRemoveGroupDialog(true);
  };

  const removeGroup = () => {
    if (groupToRemove === null) return;

    const index = groupToRemove;
    const removedGroup = formData.groups[index];
    const newGroups = formData.groups.filter((_, i) => i !== index);
    const updatedGroups = newGroups.map((group, idx) => ({
      ...group,
      order_number: idx + 1,
    }));
    setFormData({ ...formData, groups: updatedGroups });

    if (expandedGroupId === removedGroup.group_id) {
      setExpandedGroupId(null);
    }

    setGroupTests((prev) => {
      const updated = { ...prev };
      delete updated[removedGroup.group_id];
      return updated;
    });

    setLoadingTests((prev) => {
      const updated = { ...prev };
      delete updated[removedGroup.group_id];
      return updated;
    });

    setTestWeightages((prev) => {
      const updated = { ...prev };
      delete updated[removedGroup.group_id];
      return updated;
    });

    setShowRemoveGroupDialog(false);
    setGroupToRemove(null);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    const reorderedGroups = Array.from(formData.groups);
    const [removed] = reorderedGroups.splice(sourceIndex, 1);
    reorderedGroups.splice(destinationIndex, 0, removed);

    const updatedGroups = reorderedGroups.map((group, idx) => ({
      ...group,
      order_number: idx + 1,
    }));

    setFormData({ ...formData, groups: updatedGroups });

    if (isEditMode) {
      setGroupRules({});
      setRuleErrors({});
      setHasReordered(true);

      toast({
        title: 'Groups Reordered',
        description:
          'Group order has been updated. All rules have been reset. Please save your changes.',
        variant: 'default',
      });
    } else {
      toast({
        title: 'Groups Reordered',
        description: 'Group order has been updated successfully.',
        variant: 'success',
      });
    }
  };

  const handleWeightageChange = (groupId, testId, field, value) => {
    const parsed = parseFloat(value);
    const numValue = Number.isNaN(parsed) ? 0 : parsed;

    setTestWeightages((prev) => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        [testId]: {
          ...prev[groupId]?.[testId],
          [field]: numValue,
        },
      },
    }));
  };

  const handleUpdateRules = async () => {
    const hasErrors = Object.values(ruleErrors).some((error) => error);

    if (hasErrors) {
      toast({
        title: 'Validation Error',
        description: 'Please fix all rule errors before updating',
        variant: 'destructive',
      });
      return;
    }

    const rulesPayload = {
      rules: formData.groups.map((group) => ({
        group_id: group.group_id,
        order_number: group.order_number,
        rule_expression: groupRules[group.group_id] || null,
      })),
    };

    setIsUpdatingRules(true);
    try {
      const response = await TestService.updateCollectionRules(
        formData.collection_id,
        rulesPayload
      );

      if (response.status_code === 200 || response.status === 201) {
        toast({
          title: 'Success',
          description: 'Group rules updated successfully',
          variant: 'success',
        });

        setInitialGroupRules(JSON.parse(JSON.stringify(groupRules)));
      }
    } catch (error) {
      console.error('Error updating rules:', error);
      toast({
        title: 'Error',
        description:
          error.response?.data?.message ||
          'Failed to update group rules. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingRules(false);
    }
  };

  const calculateTestPercentageInGroup = (groupId, testId) => {
    const groupWeightages = testWeightages[groupId] || {};
    const totalWeightage = Object.values(groupWeightages).reduce(
      (sum, w) => sum + (w?.group_score_weight || 0),
      0
    );

    if (totalWeightage === 0) return '0.0';

    const testWeightage = groupWeightages[testId]?.group_score_weight || 0;
    const percentage = (testWeightage / totalWeightage) * 100;

    return percentage.toFixed(1);
  };

  const calculateTestPercentageInCollection = () => {
    let totalCollectionWeight = 0;

    Object.keys(groupTests).forEach((groupId) => {
      const tests = groupTests[groupId] || [];
      tests.forEach((test) => {
        const weight =
          testWeightages[groupId]?.[test.id]?.total_score_weight || 0;
        totalCollectionWeight += weight;
      });
    });

    return totalCollectionWeight;
  };

  const calculateIndividualTestPercentageInCollection = (groupId, testId) => {
    const totalCollectionWeight = calculateTestPercentageInCollection();

    if (totalCollectionWeight === 0) return '0.0';

    const testWeight =
      testWeightages[groupId]?.[testId]?.total_score_weight || 0;
    const percentage = (testWeight / totalCollectionWeight) * 100;

    return percentage.toFixed(1);
  };

  const getTotalTestsInCollection = () => {
    let totalTests = 0;
    formData.groups.forEach((group) => {
      const tests = groupTests[group.group_id] || [];
      totalTests += tests.length;
    });
    return totalTests;
  };

  const getButtonText = () =>
    isEditMode ? 'Update Collection' : 'Create Collection';
  const buttonText = getButtonText();
  const loadingText = isEditMode ? 'Updating...' : 'Creating...';

  const isButtonDisabled =
    loading ||
    (isEditMode && unsavedChangesCount === 0 && !hasReordered) ||
    (formData.groups.length > 0 && !formData.groups[0].test_ref);

  const getWeightageStyle = (value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue === 0) {
      return 'bg-[#FEF2F2] border-[rgba(220,38,38,.3)] text-[#DC2626]';
    }
    if (numValue <= 1) {
      return 'bg-brand-bg-subtle border-[rgba(32,104,178,.25)] text-primary';
    }
    return 'bg-[#ECFDF5] border-[rgba(13,150,104,.25)] text-[#0D9668]';
  };

  const getGroupColorStyle = (groupIndex) => {
    const colors = [
      {
        bg: '#E8F3FD',
        border: '#2168B222',
        text: '#2168B2',
        badgeBg: '#E8F3FD',
      },
      {
        bg: '#F5F3FF',
        border: '#7C3AED22',
        text: '#7C3AED',
        badgeBg: '#EDE9FE',
      },
      {
        bg: '#ECFDF5',
        border: '#0D966822',
        text: '#0D9668',
        badgeBg: '#D1FAE5',
      },
      {
        bg: '#FFFBEB',
        border: '#B4530922',
        text: '#B45309',
        badgeBg: '#FEF3C7',
      },
      {
        bg: '#FEF2F2',
        border: '#DC262622',
        text: '#DC2626',
        badgeBg: '#FEE2E2',
      },
      {
        bg: 'var(--brand-bg-subtle)',
        border: '#2168B220',
        text: '#2168B2',
        badgeBg: 'var(--brand-bg-subtle)',
      },
      {
        bg: '#F0FFF4',
        border: '#16A34A22',
        text: '#16A34A',
        badgeBg: '#DCFCE7',
      },
      {
        bg: '#FFF7ED',
        border: '#C2410C22',
        text: '#C2410C',
        badgeBg: '#FFEDD5',
      },
      {
        bg: '#F5F3FF',
        border: '#6D28D922',
        text: '#6D28D9',
        badgeBg: '#EDE9FE',
      },
      {
        bg: '#ECFEFF',
        border: '#0891B222',
        text: '#0891B2',
        badgeBg: '#CFFAFE',
      },
    ];
    return colors[groupIndex % colors.length];
  };

  const getChartColors = () => [
    'hsl(210, 100%, 56%)',
    'hsl(217, 91%, 60%)',
    'hsl(220, 90%, 50%)',
    'hsl(207, 90%, 54%)',
    'hsl(199, 89%, 48%)',
    'hsl(211, 100%, 43%)',
    'hsl(205, 82%, 65%)',
    'hsl(214, 95%, 36%)',
    'hsl(202, 83%, 41%)',
    'hsl(208, 100%, 50%)',
  ];

  const renderGroupTests = (group) => {
    if (loadingTests[group.group_id]) {
      return (
        <div className="flex items-center justify-center py-5">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="ml-2 text-[13px] text-muted-foreground">
            Loading tests…
          </span>
        </div>
      );
    }

    if (groupTests[group.group_id]?.length > 0) {
      const tests = groupTests[group.group_id];
      const testColors = [
        '#3B82F6',
        '#10B981',
        '#F59E0B',
        '#EF4444',
        '#8B5CF6',
        '#0891B2',
        '#14B8A6',
        '#E11D48',
        '#7C3AED',
        '#2168B2',
      ];
      const totalWeight = tests.reduce(
        (sum, t) =>
          sum +
          (testWeightages[group.group_id]?.[t.id]?.group_score_weight ?? 1),
        0
      );

      return (
        <div>
          {/* Horizontal stacked weight bar */}
          <div className="mx-[18px] mt-4 mb-1 bg-brand-bg-subtle rounded-[10px] p-3">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-[13px] font-[600] text-foreground-strong">
                Group weight distribution
              </span>
              <span className="text-[12.5px] font-[600] text-[#0D9668]">
                Total weight {totalWeight}
              </span>
            </div>
            <div className="h-3 rounded-full overflow-visible flex gap-0.5">
              {tests.map((test, ti) => {
                const w =
                  testWeightages[group.group_id]?.[test.id]
                    ?.group_score_weight ?? 1;
                const pct = totalWeight > 0 ? (w / totalWeight) * 100 : 0;
                const color = testColors[ti % testColors.length];
                return (
                  <div
                    key={test.id}
                    className="relative group/seg h-3 rounded-sm first:rounded-l-full last:rounded-r-full cursor-default"
                    style={{ width: `${pct}%`, background: color }}
                  >
                    <div className="pointer-events-none absolute bottom-[calc(100%+6px)] left-1/2 -translate-x-1/2 z-50 opacity-0 group-hover/seg:opacity-100 transition-opacity duration-150">
                      <div className="bg-popover border border-border rounded-[8px] shadow-md px-2.5 py-1.5 whitespace-nowrap min-w-[120px]">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: color }}
                          />
                          <span className="text-[12px] font-[580] text-foreground-strong truncate max-w-[180px]">
                            {test.name || test.title}
                          </span>
                        </div>
                        <div className="text-[11px] text-muted-foreground pl-[14px]">
                          Weight:{' '}
                          <span className="font-[600] text-foreground">
                            {w}
                          </span>
                          <span className="mx-1">·</span>
                          <span className="font-[600] text-foreground">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="w-2 h-2 bg-popover border-r border-b border-border rotate-45 mx-auto -mt-[5px]" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tests table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="text-left px-4 py-[13px] text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.06em] bg-card whitespace-nowrap border-b border-border w-[40%]">
                    Test Name
                  </th>
                  <th className="text-center px-4 py-[13px] text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.06em] bg-card whitespace-nowrap border-b border-border w-[14%]">
                    Max Score
                  </th>
                  <th className="text-center px-4 py-[13px] text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.06em] bg-card whitespace-nowrap border-b border-border w-[20%]">
                    Group Weightage
                  </th>
                  <th className="text-right px-4 py-[13px] text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.06em] bg-card whitespace-nowrap border-b border-border w-[14%]">
                    % in Group
                  </th>
                </tr>
              </thead>
              <tbody>
                {tests.map((test, testIndex) => {
                  const groupWeightage =
                    testWeightages[group.group_id]?.[test.id]
                      ?.group_score_weight ?? 1;
                  const percentage = calculateTestPercentageInGroup(
                    group.group_id,
                    test.id
                  );
                  const dotColor = testColors[testIndex % testColors.length];

                  return (
                    <tr
                      key={test.id || testIndex}
                      className="border-b border-border"
                    >
                      <td className="px-[18px] py-3 align-middle text-left">
                        <div className="flex items-center gap-2.5">
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: dotColor }}
                          />
                          <div>
                            <div className="text-[13px] font-[500] text-foreground-strong">
                              {test.name || 'Untitled Test'}
                            </div>
                            <div className="text-[11px] text-primary font-mono tracking-[.02em] mt-0.5">
                              {test.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3.5 py-3 text-center align-middle">
                        <span className="text-[13px] font-[500] text-foreground-strong tabular-nums">
                          {test.max_score ?? 0}
                        </span>
                      </td>
                      <td className="px-3.5 py-3 text-center align-middle">
                        <Input
                          type="number"
                          min="0"
                          max="10"
                          step="0.1"
                          value={groupWeightage}
                          onChange={(e) =>
                            handleWeightageChange(
                              group.group_id,
                              test.id,
                              'group_score_weight',
                              e.target.value
                            )
                          }
                          disabled={viewOnly}
                          readOnly={viewOnly}
                          className={`w-20 mx-auto text-center font-semibold ${getWeightageStyle(groupWeightage)}`}
                        />
                      </td>
                      <td className="px-[18px] py-3 text-right align-middle">
                        <span className="text-[13px] font-[500] text-foreground-strong tabular-nums">
                          {percentage}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-muted/30">
                  <td colSpan="4" className="px-[18px] py-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-[11px] font-[600] text-muted-foreground uppercase tracking-[.05em] mb-1">
                          Group score rule
                        </div>
                        <div className="text-[12px] text-primary font-mono leading-relaxed">
                          Sum(norm_score × group_weight) / Sum(group_weight)
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] font-[600] text-muted-foreground uppercase tracking-[.05em] mb-1">
                          Active tests
                        </div>
                        <div className="text-[13px] font-[600] text-foreground-strong">
                          {tests.length} of {tests.length}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div className="text-center py-5">
        <p className="text-[13px] text-muted-foreground">
          No tests found in this group
        </p>
      </div>
    );
  };

  const descLen = (formData.description || '').length;

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-[760px] mx-auto space-y-[18px] min-h-[calc(100vh-3.4rem)] px-8 pb-20"
    >
      {/* ── 1. Collection Details ── */}
      <div className="rounded-[10px] border border-border bg-card overflow-hidden">
        <div className="flex items-center gap-2.5 px-[18px] py-3.5 border-b border-border">
          <span className="w-[30px] h-[30px] rounded-lg bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-[15px] h-[15px]" strokeWidth={1.9} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-[650] text-foreground-strong leading-tight">
              Collection details
            </div>
            <div className="text-[12px] text-muted-foreground leading-snug mt-0.5">
              {formData.collection_id ? (
                <span className="font-mono tracking-[.02em]">
                  #{formData.collection_id}
                </span>
              ) : (
                <>Name and describe this assessment plan</>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowWeightageGuide((v) => !v)}
            className="inline-flex items-center gap-[6px] bg-transparent border-none p-0 cursor-pointer text-[12.5px] font-[600] text-primary transition-colors hover:text-primary/80 flex-shrink-0"
          >
            <Info className="h-3.5 w-3.5" strokeWidth={1.8} />
            {showWeightageGuide ? 'Hide' : 'Show'} weightage guide
          </button>
        </div>
        <div className="p-[18px] space-y-4">
          {/* Name */}
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              htmlFor="collection-name"
              className="block text-[12.5px] font-[560] text-foreground mb-1.5"
            >
              Name <span className="text-destructive">*</span>
            </label>
            <input
              id="collection-name"
              type="text"
              maxLength={80}
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              disabled={viewOnly}
              placeholder="Enter collection name"
              className={fieldBase}
            />
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="collection-desc"
                className="text-[12.5px] font-[560] text-foreground"
              >
                Description
              </label>
              <span
                className={`text-[11.5px] font-medium tabular-nums ${
                  descLen >= 240 ? 'text-destructive' : 'text-muted-foreground'
                }`}
              >
                {descLen} / 240
              </span>
            </div>
            <textarea
              id="collection-desc"
              maxLength={240}
              rows={3}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              disabled={viewOnly}
              placeholder="Enter collection description (optional)"
              className={`${fieldBase} resize-none leading-relaxed`}
            />
          </div>

          {/* Weightage guide */}
          {showWeightageGuide && (
            <div className="pt-2 border-t border-border">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-brand-bg-subtle border border-primary/10 rounded-lg p-3">
                  <div className="text-[12px] font-semibold text-foreground-strong mb-1.5">
                    Group Weightage
                  </div>
                  <ul className="text-[12px] text-muted-foreground space-y-1 list-none">
                    <li>0 = Excluded from group</li>
                    <li>0.5 = Low importance</li>
                    <li>1.0 = Standard (default)</li>
                    <li>2.0+ = Higher emphasis</li>
                  </ul>
                </div>
                <div className="bg-brand-bg-subtle border border-primary/10 rounded-lg p-3">
                  <div className="text-[12px] font-semibold text-foreground-strong mb-1.5">
                    Collection Weightage
                  </div>
                  <p className="text-[12px] text-muted-foreground leading-relaxed">
                    Independent of group weight. Use 0–5 scale. 0 excludes from
                    collection total.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── 2. Test Groups ── */}
      <div className="rounded-[10px] border border-border bg-card overflow-hidden">
        {/* Section header */}
        <div className="flex items-center gap-2.5 px-[18px] py-3.5 border-b border-border">
          <span className="w-[30px] h-[30px] rounded-lg bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
            <Scale className="w-[15px] h-[15px]" strokeWidth={1.9} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-[650] text-foreground-strong leading-tight">
              Test groups
            </div>
            <div className="text-[12px] text-muted-foreground leading-snug mt-0.5">
              Drag to reorder · weights are proportional (0–5)
            </div>
          </div>
          {formData.groups.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-semibold bg-brand-bg-subtle text-primary border border-primary/10">
              {formData.groups.length}
            </span>
          )}
          {/* Toggle button in header (always visible) */}
          {activeTab === 'rules' ? (
            <button
              type="button"
              onClick={() => setShowRuleSyntaxGuide((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-[6px] rounded-[7px] border border-input text-muted-foreground text-[12px] font-[550] cursor-pointer transition-all hover:border-primary hover:text-primary hover:bg-brand-bg-subtle flex-shrink-0"
            >
              {showRuleSyntaxGuide ? (
                <EyeOff className="h-3.5 w-3.5" strokeWidth={1.8} />
              ) : (
                <Eye className="h-3.5 w-3.5" strokeWidth={1.8} />
              )}
              {showRuleSyntaxGuide ? 'Hide' : 'Show'} syntax guide
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowChart((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-[6px] rounded-[7px] border border-input text-muted-foreground text-[12px] font-[550] cursor-pointer transition-all hover:border-primary hover:text-primary hover:bg-brand-bg-subtle flex-shrink-0"
            >
              {showChart ? (
                <EyeOff className="h-3.5 w-3.5" strokeWidth={1.8} />
              ) : (
                <Eye className="h-3.5 w-3.5" strokeWidth={1.8} />
              )}
              {showChart ? 'Hide' : 'Show'} chart
            </button>
          )}
        </div>

        {/* Underline tab strip */}
        {(isEditMode || formData.groups.length > 0) && (
          <div className="flex items-center bg-brand-bg-subtle border-b border-border px-[18px]">
            {[
              { key: 'groups', label: 'Groups' },
              { key: 'tests', label: 'All tests' },
              { key: 'rules', label: 'Group rules' },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => handleTabChange(key)}
                className={`relative px-[14px] py-[11px] text-[13px] font-[550] transition-colors whitespace-nowrap ${
                  activeTab === key
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
                {activeTab === key && (
                  <span className="absolute bottom-0 inset-x-0 h-0.5 bg-primary" />
                )}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="p-[18px]">
          {/* ── Groups Tab ── */}
          {activeTab === 'groups' && (
            <>
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  {formData.groups.length > 1 && !viewOnly && (
                    <span className="text-[11.5px] text-muted-foreground flex items-center gap-1">
                      <GripVertical className="h-3 w-3" />
                      Drag to reorder
                    </span>
                  )}
                </div>
                {!viewOnly && (
                  <Button
                    type="button"
                    size="sm"
                    variant="addField"
                    className="text-xs"
                    onClick={() => setShowGroupModal(true)}
                    disabled={loading}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Test Groups
                  </Button>
                )}
              </div>

              {formData.groups.length > 0 &&
                !formData.groups[0].test_ref &&
                !viewOnly && (
                  <div className="mb-4 bg-[#FEF2F2] border border-[rgba(220,38,38,.15)] rounded-lg px-3.5 py-2.5 flex items-start gap-2">
                    <Info
                      className="h-3.5 w-3.5 text-[#DC2626] mt-0.5 flex-shrink-0"
                      strokeWidth={2}
                    />
                    <p className="text-[12.5px] text-[#DC2626]">
                      <span className="font-semibold">Required:</span> Add a
                      test reference to the first group to continue.
                    </p>
                  </div>
                )}

              {formData.groups.length === 0 ? (
                <div className="border-2 border-dashed border-input rounded-[10px] p-10 text-center">
                  <p className="text-[14px] font-semibold text-foreground-strong mb-1">
                    No test groups added yet
                  </p>
                  <p className="text-[12.5px] text-muted-foreground">
                    Click &quot;Add Test Groups&quot; to select groups from the
                    library.
                  </p>
                </div>
              ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="groups-list">
                    {(droppableProvided) => (
                      <div
                        ref={droppableProvided.innerRef}
                        className="flex flex-col gap-2"
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...droppableProvided.droppableProps}
                      >
                        {formData.groups.map((group, index) => {
                          const testsArr = groupTests[group.group_id];
                          const testsCount =
                            testsArr != null ? testsArr.length : null;

                          return (
                            <Draggable
                              key={
                                group.group_id || `new-${group.order_number}`
                              }
                              draggableId={`group-${group.group_id || group.order_number}`}
                              index={index}
                              isDragDisabled={viewOnly || loading}
                            >
                              {(draggableProvided, snapshot) => (
                                <div
                                  ref={draggableProvided.innerRef}
                                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                                  {...draggableProvided.draggableProps}
                                  className={`border border-border rounded-[10px] overflow-hidden transition-shadow ${
                                    snapshot.isDragging
                                      ? 'shadow-lg border-primary/30'
                                      : ''
                                  }`}
                                >
                                  {/* ghead */}
                                  <div
                                    className="flex items-center gap-[11px] px-3 py-[11px] bg-card group/row cursor-pointer select-none"
                                    onClick={() =>
                                      toggleGroupExpand(group.group_id)
                                    }
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) =>
                                      e.key === 'Enter' &&
                                      toggleGroupExpand(group.group_id)
                                    }
                                  >
                                    {!viewOnly && (
                                      <button
                                        type="button"
                                        aria-label="Drag to reorder group"
                                        className="cursor-grab active:cursor-grabbing p-0.5 rounded text-muted-foreground flex-shrink-0"
                                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                                        {...draggableProvided.dragHandleProps}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <GripVertical className="h-4 w-4" />
                                      </button>
                                    )}

                                    {/* Order badge */}
                                    <div className="w-[22px] h-[22px] rounded-[6px] bg-brand-bg-subtle text-primary flex items-center justify-center text-[11px] font-mono font-bold flex-shrink-0">
                                      {index + 1}
                                    </div>

                                    {/* Group info */}
                                    <div className="flex-1 min-w-0 text-left">
                                      <div className="text-[13px] font-[600] text-foreground-strong leading-tight">
                                        {group.name ||
                                          `Group ${group.group_id}`}
                                      </div>
                                      {group.description && (
                                        <div className="text-[12px] text-muted-foreground leading-snug mt-0.5">
                                          {group.description}
                                        </div>
                                      )}
                                      <div className="text-[11.5px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                                        {testsCount !== null ? (
                                          <span>
                                            {testsCount} of {testsCount} test
                                            {testsCount !== 1 ? 's' : ''} active
                                          </span>
                                        ) : (
                                          <span>…</span>
                                        )}
                                        <span>·</span>
                                        <span className="font-mono text-[11px] text-brand-deep bg-brand-bg-subtle px-[5px] rounded-[4px]">
                                          Ref: {group.test_ref || '—'}
                                        </span>
                                      </div>
                                    </div>

                                    {/* expand chevron */}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleGroupExpand(group.group_id);
                                      }}
                                      className="w-[32px] h-[32px] rounded-[8px] border border-border bg-card text-muted-foreground flex items-center justify-center cursor-pointer hover:bg-muted transition-colors flex-shrink-0"
                                    >
                                      <ChevronDown
                                        className={`h-[15px] w-[15px] transition-transform ${expandedGroupId === group.group_id ? 'rotate-180' : ''}`}
                                        strokeWidth={2}
                                      />
                                    </button>
                                  </div>

                                  {/* gbody – expanded accordion */}
                                  {expandedGroupId === group.group_id && (
                                    <div className="border-t border-border">
                                      {/* Action row: ref + test type (left) + delete group (right) */}
                                      <div className="flex items-center justify-between gap-3 flex-wrap px-[18px] py-3 border-b border-border">
                                        {/* Left: test reference + test type */}
                                        <div className="flex items-center gap-5 flex-wrap">
                                          <div className="flex items-center gap-2.5">
                                            <span className="text-[12px] font-[560] text-muted-foreground whitespace-nowrap">
                                              Test reference
                                              {index === 0 && (
                                                <span className="text-destructive ml-0.5">
                                                  *
                                                </span>
                                              )}
                                            </span>
                                            {viewOnly && group.test_ref && (
                                              <span className="font-mono text-[12px] font-semibold text-brand-deep bg-brand-bg-subtle px-[7px] py-[3px] rounded-[5px]">
                                                {group.test_ref}
                                              </span>
                                            )}
                                            {viewOnly && !group.test_ref && (
                                              <span className="text-[12px] text-muted-foreground">
                                                —
                                              </span>
                                            )}
                                            {!viewOnly && (
                                              <div className="flex items-center gap-1.5">
                                                <input
                                                  type="text"
                                                  value={group.test_ref || ''}
                                                  onChange={(e) =>
                                                    handleTestRefChange(
                                                      index,
                                                      e.target.value
                                                    )
                                                  }
                                                  className="px-2.5 py-1.5 rounded-[7px] border border-input font-mono text-[12.5px] text-foreground-strong bg-card placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] w-[160px]"
                                                  placeholder="Enter ref…"
                                                />
                                                {group.test_ref && (
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      handleTestRefChange(
                                                        index,
                                                        ''
                                                      )
                                                    }
                                                    className="w-[28px] h-[28px] rounded-[7px] border border-border bg-card text-muted-foreground flex items-center justify-center hover:border-destructive hover:text-destructive hover:bg-destructive/5 transition-colors"
                                                  >
                                                    <Trash2
                                                      className="h-3.5 w-3.5"
                                                      strokeWidth={1.8}
                                                    />
                                                  </button>
                                                )}
                                              </div>
                                            )}
                                          </div>

                                          {/* Test type — optional, from GET /internal/assessment-types */}
                                          <div className="flex items-center gap-2.5">
                                            <span className="text-[12px] font-[560] text-muted-foreground whitespace-nowrap">
                                              Test type
                                            </span>
                                            {viewOnly &&
                                              group.test_type_code && (
                                                <span className="font-mono text-[12px] font-semibold text-brand-deep bg-brand-bg-subtle px-[7px] py-[3px] rounded-[5px]">
                                                  {getAssessmentTypeLabel(
                                                    group.test_type_code
                                                  )}
                                                </span>
                                              )}
                                            {viewOnly &&
                                              !group.test_type_code && (
                                                <span className="text-[12px] text-muted-foreground">
                                                  —
                                                </span>
                                              )}
                                            {!viewOnly && (
                                              <div className="flex items-center gap-1.5">
                                                <Select
                                                  value={
                                                    group.test_type_code || ''
                                                  }
                                                  onValueChange={(value) =>
                                                    handleTestTypeCodeChange(
                                                      index,
                                                      value
                                                    )
                                                  }
                                                >
                                                  <SelectTrigger className="h-[34px] w-[200px] text-[12.5px]">
                                                    <SelectValue placeholder="Select test type…" />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    {selectableAssessmentTypes.map(
                                                      (type) => (
                                                        <SelectItem
                                                          key={type.code}
                                                          value={type.code}
                                                        >
                                                          {type.display_name}
                                                        </SelectItem>
                                                      )
                                                    )}
                                                  </SelectContent>
                                                </Select>
                                                {group.test_type_code && (
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      handleTestTypeCodeChange(
                                                        index,
                                                        ''
                                                      )
                                                    }
                                                    className="w-[28px] h-[28px] rounded-[7px] border border-border bg-card text-muted-foreground flex items-center justify-center hover:border-destructive hover:text-destructive hover:bg-destructive/5 transition-colors"
                                                  >
                                                    <Trash2
                                                      className="h-3.5 w-3.5"
                                                      strokeWidth={1.8}
                                                    />
                                                  </button>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        {/* Right: delete group */}
                                        {!viewOnly && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleRemoveGroupClick(index)
                                            }
                                            disabled={loading}
                                            className="inline-flex items-center gap-1.5 bg-transparent border-none p-0 cursor-pointer text-[12.5px] font-[600] text-[#DC2626] hover:text-[#DC2626]/80 transition-colors"
                                          >
                                            <Trash2
                                              className="h-[14px] w-[14px]"
                                              strokeWidth={1.8}
                                            />
                                            Delete group
                                          </button>
                                        )}
                                      </div>
                                      {/* Tests table + chart */}
                                      {renderGroupTests(group)}
                                    </div>
                                  )}
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </>
          )}

          {/* ── All Tests Tab ── */}
          {activeTab === 'tests' && (
            <div>
              {formData.groups.length === 0 ? (
                <div className="border-2 border-dashed border-input rounded-[10px] p-10 text-center">
                  <p className="text-[14px] font-semibold text-foreground-strong mb-1">
                    No groups added yet
                  </p>
                  <p className="text-[12.5px] text-muted-foreground">
                    Switch to the Groups tab to add test groups.
                  </p>
                </div>
              ) : (
                <>
                  {showChart && (
                    <div className="mb-6 bg-card border border-border rounded-lg p-4">
                      <h3 className="text-[12px] font-semibold text-muted-foreground uppercase tracking-[.06em] mb-4">
                        Collection Weightage Distribution
                      </h3>
                      {(() => {
                        const colors = getChartColors();
                        const chartData = [];
                        const chartConfig = {};
                        let colorIndex = 0;

                        formData.groups.forEach((group) => {
                          const tests = groupTests[group.group_id] || [];
                          tests.forEach((test) => {
                            const weightage =
                              testWeightages[group.group_id]?.[test.id]
                                ?.total_score_weight || 1;

                            chartData.push({
                              name:
                                test.name?.substring(0, 15) ||
                                `Test ${test.id}`,
                              weightage,
                              fill: colors[colorIndex % colors.length],
                            });
                            colorIndex += 1;
                          });
                        });

                        if (chartData.length === 0) return null;

                        chartConfig.weightage = {
                          label: 'Collection Weightage',
                        };

                        return (
                          <div className="h-[350px]">
                            <ChartContainer config={chartConfig}>
                              <ResponsiveContainer width="100%" height="50%">
                                <BarChart
                                  data={chartData}
                                  margin={{
                                    top: 20,
                                    right: 10,
                                    left: 10,
                                    bottom: 60,
                                  }}
                                >
                                  <CartesianGrid
                                    strokeDasharray="2 2"
                                    vertical={false}
                                  />
                                  <XAxis
                                    dataKey="name"
                                    angle={-45}
                                    textAnchor="end"
                                    height={300}
                                    tick={{ fontSize: 11 }}
                                  />
                                  <YAxis tick={{ fontSize: 11 }} />
                                  <ChartTooltip
                                    cursor={false}
                                    content={<ChartTooltipContent />}
                                  />
                                  <Bar
                                    dataKey="weightage"
                                    radius={[4, 4, 0, 0]}
                                  >
                                    <LabelList
                                      dataKey="weightage"
                                      position="top"
                                      style={{
                                        fontSize: '11px',
                                        fontWeight: 'bold',
                                      }}
                                    />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </ChartContainer>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-border">
                      <thead>
                        <tr>
                          <th className="text-left px-3.5 py-2.5 text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[.06em] bg-card border-b border-border w-[30%]">
                            Test Name
                          </th>
                          <th className="text-left px-3.5 py-2.5 text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[.06em] bg-card border-b border-border w-[25%]">
                            Group
                          </th>
                          <th className="text-center px-3.5 py-2.5 text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[.06em] bg-card border-b border-border w-[12%]">
                            Max Score
                          </th>
                          <th className="text-center px-3.5 py-2.5 text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[.06em] bg-card border-b border-border w-[18%]">
                            Collection Weightage
                          </th>
                          <th className="text-center px-3.5 py-2.5 text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[.06em] bg-card border-b border-border w-[15%]">
                            % in Collection
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.groups.map((group, groupIndex) => {
                          const tests = groupTests[group.group_id] || [];
                          if (tests.length === 0) return null;

                          const colorStyle = getGroupColorStyle(groupIndex);

                          return tests.map((test) => {
                            const collectionWeightage =
                              testWeightages[group.group_id]?.[test.id]
                                ?.total_score_weight ?? 1;
                            const collectionPercentage =
                              calculateIndividualTestPercentageInCollection(
                                group.group_id,
                                test.id
                              );

                            return (
                              <tr
                                key={`${group.group_id}-${test.id}`}
                                className="border-b border-border hover:bg-brand-bg-subtle transition-colors"
                              >
                                <td className="px-3.5 py-3 align-middle text-left">
                                  <div className="text-[13px] font-medium text-foreground-strong">
                                    {test.name || 'Untitled Test'}
                                  </div>
                                  <div className="text-[11px] text-primary mt-0.5 font-mono tracking-[.02em]">
                                    {test.id}
                                  </div>
                                </td>
                                <td className="px-3.5 py-3 align-middle text-left">
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-[550] whitespace-nowrap"
                                    style={{
                                      background: colorStyle.bg,
                                      color: colorStyle.text,
                                    }}
                                  >
                                    {group.name}
                                  </span>
                                  {group.test_ref && (
                                    <div className="text-[11px] text-muted-foreground mt-1">
                                      Ref: {group.test_ref}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3.5 py-3 text-center align-middle">
                                  <span className="inline-block px-2 py-0.5 rounded bg-muted text-muted-foreground text-[12.5px] font-medium tabular-nums">
                                    {test.max_score || 0}
                                  </span>
                                </td>
                                <td className="px-3.5 py-3 text-center align-middle">
                                  <Input
                                    type="number"
                                    min="0"
                                    max="10"
                                    step="0.1"
                                    value={collectionWeightage}
                                    onChange={(e) =>
                                      handleWeightageChange(
                                        group.group_id,
                                        test.id,
                                        'total_score_weight',
                                        e.target.value
                                      )
                                    }
                                    disabled={viewOnly}
                                    readOnly={viewOnly}
                                    className={`w-20 mx-auto text-center font-semibold ${getWeightageStyle(collectionWeightage)}`}
                                  />
                                </td>
                                <td className="px-3.5 py-3 text-center align-middle">
                                  <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-[12px] font-semibold bg-[#ECFDF5] text-[#0D9668] min-w-[60px]">
                                    {collectionPercentage}%
                                  </span>
                                </td>
                              </tr>
                            );
                          });
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-card border-t-2 border-border">
                          <td
                            colSpan="3"
                            className="px-3.5 py-3 text-right text-[12.5px] font-semibold text-foreground-strong"
                          >
                            TOTAL
                          </td>
                          <td className="px-3.5 py-3 text-center text-[12.5px] font-bold text-[#7C3AED] tabular-nums">
                            {calculateTestPercentageInCollection().toFixed(1)}
                          </td>
                          <td className="px-3.5 py-3 text-center text-[12.5px] font-bold text-[#0D9668]">
                            100.0%
                          </td>
                        </tr>
                        <tr className="border-t border-border bg-card">
                          <td colSpan="5" className="px-3.5 py-3">
                            <div className="grid grid-cols-2 gap-2.5 mt-1">
                              <div className="bg-card border border-border rounded-lg p-2.5">
                                <div className="text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[.05em] mb-1">
                                  Collection Score Rule
                                </div>
                                <div className="text-[11.5px] text-muted-foreground leading-relaxed font-mono">
                                  Sum(norm_score × coll_weight) /
                                  Sum(coll_weight)
                                </div>
                              </div>
                              <div className="bg-card border border-border rounded-lg p-2.5">
                                <div className="text-[10.5px] font-semibold text-muted-foreground uppercase tracking-[.05em] mb-1">
                                  Active Tests (Collection)
                                </div>
                                <div className="text-[13px] font-semibold text-foreground-strong">
                                  {getTotalTestsInCollection()}{' '}
                                  {getTotalTestsInCollection() === 1
                                    ? 'test'
                                    : 'tests'}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Rules Tab ── */}
          {activeTab === 'rules' && (
            <div>
              {formData.groups.length === 0 ? (
                <div className="border-2 border-dashed border-input rounded-[10px] p-10 text-center">
                  <p className="text-[14px] font-semibold text-foreground-strong mb-1">
                    No groups added yet
                  </p>
                  <p className="text-[12.5px] text-muted-foreground">
                    Switch to the Groups tab to add test groups first.
                  </p>
                </div>
              ) : (
                <div className="space-y-0">
                  {showRuleSyntaxGuide && (
                    <div className="bg-brand-bg-subtle border border-primary/10 rounded-lg p-4 mb-4">
                      <div className="text-[12px] font-[650] text-foreground-strong mb-2">
                        Rule Syntax Guide
                      </div>
                      <ul className="text-[12.5px] text-muted-foreground space-y-1.5 list-none">
                        <li>
                          • Use{' '}
                          <code className="bg-card border border-border rounded px-1 py-0.5 text-[11.5px] text-muted-foreground">
                            G1, G2, G3…
                          </code>{' '}
                          to reference groups by position (
                          <strong>uppercase G required</strong>)
                        </li>
                        <li>
                          • You can reference G1 to G
                          {formData.groups.length || 'N'} (only existing groups)
                        </li>
                        <li>
                          • A group can only reference itself and groups{' '}
                          <strong>before</strong> it
                        </li>
                        <li>
                          • Operators:{' '}
                          <code className="bg-card border border-border rounded px-1 py-0.5 text-[11.5px] text-muted-foreground">
                            AND
                          </code>
                          ,{' '}
                          <code className="bg-card border border-border rounded px-1 py-0.5 text-[11.5px] text-muted-foreground">
                            OR
                          </code>{' '}
                          — must be <strong>uppercase</strong>
                        </li>
                        <li>
                          • Example:{' '}
                          <code className="bg-card border border-border rounded px-1 py-0.5 text-[11.5px] text-muted-foreground">
                            (G1 &gt; 40 AND G1 &lt; 55) AND G2 &gt;= 4.5
                          </code>
                        </li>
                      </ul>
                    </div>
                  )}

                  <div className="space-y-2.5">
                    {formData.groups.map((group, index) => (
                      <div
                        key={group.group_id}
                        className="bg-card border border-border rounded-[9px] p-3.5 flex items-start gap-3.5"
                      >
                        <div className="w-[34px] h-[26px] rounded-[6px] bg-brand-bg-subtle text-primary flex items-center justify-center text-[11.5px] font-mono font-bold flex-shrink-0">
                          G{index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="text-[13px] font-[550] text-foreground-strong mb-2 text-left">
                            {group.name || `Group ${group.group_id}`}
                          </div>
                          <div className="relative">
                            <Input
                              type="text"
                              value={groupRules[group.group_id] || ''}
                              onChange={(e) =>
                                handleRuleChange(group.group_id, e.target.value)
                              }
                              disabled={viewOnly}
                              placeholder={`e.g. G${index + 1} > 40 AND G${index + 1} < 55`}
                              className={`w-full font-mono text-[12.5px] ${
                                ruleErrors[group.group_id]
                                  ? 'border-destructive focus:border-destructive focus:ring-destructive/[0.08]'
                                  : 'border-input'
                              }`}
                            />
                            {ruleErrors[group.group_id] && (
                              <p className="text-[11.5px] text-[#DC2626] mt-1.5 flex items-start gap-1.5 leading-snug">
                                <X
                                  className="h-3 w-3 flex-shrink-0 mt-0.5"
                                  strokeWidth={2}
                                />
                                {ruleErrors[group.group_id]}
                              </p>
                            )}
                            {!ruleErrors[group.group_id] &&
                              groupRules[group.group_id] && (
                                <p className="text-[11.5px] text-[#0D9668] mt-1.5 flex items-center gap-1.5">
                                  <Check
                                    className="h-3 w-3"
                                    strokeWidth={2.2}
                                  />
                                  Rule is valid
                                </p>
                              )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── 3. Passing Criteria ── */}
      <div className="rounded-[10px] border border-border bg-card overflow-hidden">
        <div className="flex items-center gap-2.5 px-[18px] py-3.5 border-b border-border">
          <span className="w-[30px] h-[30px] rounded-lg bg-brand-bg-subtle text-primary flex items-center justify-center flex-shrink-0">
            <Target className="w-[15px] h-[15px]" strokeWidth={1.9} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-[650] text-foreground-strong leading-tight">
              Passing criteria
            </div>
            <div className="text-[12px] text-muted-foreground leading-snug mt-0.5">
              The bar a candidate has to clear
            </div>
          </div>
        </div>
        <div className="p-[18px]">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor="cutoff-score"
            className="block text-[12.5px] font-[560] text-foreground mb-1.5"
          >
            Cutoff score
          </label>
          <div className="flex items-stretch border border-input rounded-lg overflow-hidden w-36 transition-all focus-within:border-primary focus-within:shadow-[0_0_0_3px_rgba(0,0,0,.06)]">
            <input
              id="cutoff-score"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={formData.cutoff_score}
              onChange={(e) => {
                const value =
                  e.target.value === '' ? 50 : parseFloat(e.target.value);
                setFormData({
                  ...formData,
                  cutoff_score: Number.isNaN(value) ? 50 : value,
                });
              }}
              disabled={viewOnly}
              className="px-3 py-[9px] border-none outline-none text-[14px] font-medium text-foreground-strong bg-transparent flex-1 min-w-0 disabled:opacity-50 disabled:cursor-not-allowed tabular-nums"
            />
            <span className="px-3 bg-brand-bg-subtle border-l border-input text-[13px] font-[550] text-primary flex items-center whitespace-nowrap flex-shrink-0">
              %
            </span>
          </div>
          <p className="text-[11.5px] text-muted-foreground mt-1.5">
            Minimum percentage score a candidate must achieve to pass this
            collection.
          </p>
        </div>
      </div>

      {/* ── Modals ── */}
      <TestGroupSelectionModal
        open={showGroupModal}
        onClose={() => setShowGroupModal(false)}
        onAdd={handleAddGroups}
        selectedGroupIds={formData.groups.map((g) => g.group_id)}
      />

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 'Update Collection' : 'Create Collection'}
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-3">
              <div>
                {isEditMode ? (
                  <>
                    You are about to update this test collection with{' '}
                    <span className="font-semibold text-foreground-strong">
                      {unsavedChangesCount} change
                      {unsavedChangesCount !== 1 ? 's' : ''}
                    </span>
                    . Do you want to continue?
                  </>
                ) : (
                  <>
                    You are about to create a new test collection with{' '}
                    <span className="font-semibold text-foreground-strong">
                      {formData.groups.length} group
                      {formData.groups.length !== 1 ? 's' : ''}
                    </span>{' '}
                    and{' '}
                    <span className="font-semibold text-foreground-strong">
                      {getTotalTestsInCollection()} test
                      {getTotalTestsInCollection() !== 1 ? 's' : ''}
                    </span>
                    . Do you want to continue?
                  </>
                )}
              </div>
              {hasReordered && isEditMode && (
                <div className="bg-[#FFFBEB] border border-[rgba(180,83,9,.15)] rounded-lg p-3 flex items-start gap-2">
                  <Info
                    className="h-3.5 w-3.5 text-[#B45309] mt-0.5 flex-shrink-0"
                    strokeWidth={2}
                  />
                  <p className="text-[12.5px] text-[#B45309]">
                    <span className="font-semibold">Warning:</span> You&apos;ve
                    changed the order of the groups. The existing rules have
                    been reset. Please review and recreate rules after saving.
                  </p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmedSubmit}
              disabled={loading}
              className="min-w-[120px]"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {loadingText}
                </>
              ) : (
                'Continue'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Group Confirmation Dialog */}
      <Dialog
        open={showRemoveGroupDialog}
        onOpenChange={setShowRemoveGroupDialog}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Test Group</DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to remove{' '}
              <span className="font-semibold text-foreground-strong">
                {groupToRemove !== null && formData.groups[groupToRemove]
                  ? formData.groups[groupToRemove].name ||
                    `Group ${formData.groups[groupToRemove].group_id}`
                  : 'this group'}
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowRemoveGroupDialog(false);
                setGroupToRemove(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={removeGroup}
              disabled={loading}
              className="min-w-[120px]"
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Sticky footer ── */}
      {!viewOnly && (
        <div className="sticky bottom-0 z-20 flex items-center justify-between gap-2 py-[14px] px-8 -mx-8 mt-6 bg-muted backdrop-blur-[6px]">
          {/* Status */}
          <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
            {hasReordered && isEditMode && (
              <>
                <Info
                  className="h-3.5 w-3.5 text-[#B45309] flex-shrink-0"
                  strokeWidth={1.8}
                />
                <span>
                  Groups reordered — all rules reset. Save your changes.
                </span>
              </>
            )}
            {!hasReordered &&
              isEditMode &&
              activeTab === 'rules' &&
              hasUnsavedRuleChanges && (
                <>
                  <Info
                    className="h-3.5 w-3.5 text-[#B45309] flex-shrink-0"
                    strokeWidth={1.8}
                  />
                  <span>You have unsaved rule changes</span>
                </>
              )}
            {!hasReordered &&
              isEditMode &&
              activeTab !== 'rules' &&
              unsavedChangesCount > 0 && (
                <>
                  <Info
                    className="h-3.5 w-3.5 text-[#B45309] flex-shrink-0"
                    strokeWidth={1.8}
                  />
                  <span>
                    You have {unsavedChangesCount} unsaved{' '}
                    {unsavedChangesCount === 1 ? 'change' : 'changes'}
                  </span>
                </>
              )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2.5">
            {activeTab === 'rules' ? (
              <Button
                size="sm"
                type="button"
                onClick={handleUpdateRules}
                className="gap-2"
                disabled={loading || !formData.collection_id || isUpdatingRules}
              >
                {isUpdatingRules ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Updating…
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Update Rules
                  </>
                )}
              </Button>
            ) : (
              <Button size="sm" type="submit" disabled={isButtonDisabled}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {loadingText}
                  </>
                ) : (
                  buttonText
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </form>
  );
};
